import { useAtomValue } from 'jotai'
import { addMilliseconds, set, differenceInMilliseconds } from 'date-fns'
import { formatDurationHuman } from '../../../utils'
import { rundownAtom } from '../../../store/rundown.store'
import { getNowAtom, runnerAtom } from '../../../store/runner.store'
import { applyDate, moveAfterWithTolerance } from '~shared/timeutils'
import { CUE_OVERLAP_TOLERANCE } from '~shared/consts'
import { timestampsAtom } from '../../../store/timestamps.store.js'

/**
 * OverUnder Component
 *
 * Displays the "over/under" status for a rundown, comparing the actual end time
 * with the planned or original end time. Shows whether the rundown is early,
 * on time, or late.
 *
 * Features:
 * - Calculates the timing difference dynamically.
 * - Formats the difference as "early," "on time," or "late".
 */
export default function OverUnder() {
  const timestamps = useAtomValue(timestampsAtom)
  const rundown = useAtomValue(rundownAtom)
  const runner = useAtomValue(runnerAtom)
  const getNow = useAtomValue(getNowAtom)

  if (!timestamps) return null

  const actualEnd = addMilliseconds(
    timestamps.actual.start,
    timestamps.actual.duration,
  )
  const originalEnd = addMilliseconds(
    timestamps.original.start,
    timestamps.original.duration,
  )

  // Move to after actual end, moving rundown end time to today (like timestamps logic: everything moves to today) and use tolerance to allow being late.
  const plannedEnd = moveAfterWithTolerance(
    applyDate(rundown.endTime, new Date(getNow()), {
      timezone: rundown.timezone,
    }),
    actualEnd,
    CUE_OVERLAP_TOLERANCE,
    { timezone: rundown.timezone },
  )

  // Depending if the rundown is running or being edited, it compares against the
  // original end (last cue original end) or the planned rundown end.
  const compareTo = runner ? originalEnd : plannedEnd

  // Project timestamps onto the current day.
  // For our use case only time matters, and not the date.
  const compareToTodayProjection = set(new Date(getNow()), {
    hours: compareTo.getHours(),
    minutes: compareTo.getMinutes(),
    seconds: compareTo.getSeconds(),
    milliseconds: compareTo.getMilliseconds(),
  })
  const actualEndTodayProjection = set(new Date(getNow()), {
    hours: actualEnd.getHours(),
    minutes: actualEnd.getMinutes(),
    seconds: actualEnd.getSeconds(),
    milliseconds: actualEnd.getMilliseconds(),
  })

  // Using `differenceInMilliseconds` for more bulletproof approach compared to negation
  const diff = differenceInMilliseconds(
    compareToTodayProjection,
    actualEndTodayProjection,
  )

  return (
    <section>
      <div
        className={[
          'pl-1.5 text-gray-500 font-semibold whitespace-pre text-[10px]',
        ].join(' ')}
      >
        <p>Over / Under</p>
      </div>
      <p className="h-6 px-1.5 flex items-center font-mono tabular-nums">
        {diff >= 1000 ? (
          <span className="text-green-500">
            {formatDurationHuman(diff)} early
          </span>
        ) : (
          ''
        )}
        {Math.abs(diff) < 1000 ? (runner ? '--' : 'On time') : ''}
        {diff <= -1000 ? (
          <span className="text-red-500">{formatDurationHuman(diff)} late</span>
        ) : (
          ''
        )}
      </p>
    </section>
  )
}
